"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Star } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
function TestimonialConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="w-full max-w-4xl mx-auto py-12">
        <div className="animate-pulse flex flex-col items-center space-y-6">
          <div className="h-8 w-48 bg-muted rounded-md"></div>
          <div className="h-32 w-full max-w-2xl bg-muted rounded-xl"></div>
          <div className="size-16 bg-muted rounded-lg"></div>
        </div>
      </div>;
  }
  if (error || posts.length === 0) {
    return null;
  }
  return <Carousel opts={{
    align: "center",
    loop: true
  }} className="w-full max-w-5xl mx-auto px-12 md:px-16">
      <CarouselContent>
        {posts.map((post, i) => {
        const rating = Number(post.customFields?.star_rating) || 5;
        const authorName = post.customFields?.testimonial_author || post.title;
        const authorRole = post.customFields?.author_role;
        const quote = post.customFields?.testimonial_quote || post.excerpt?.replace(/(<([^>]+)>)/gi, "");
        const imageUrl = post.featuredImage || post.customFields?.featured_image;
        return <CarouselItem key={post.id} data-index={i}>
              <Link to={post.link || "#"} className="block group focus:outline-none">
                <div className="flex flex-col items-center text-center px-4 py-8 md:py-12 transition-transform duration-300 group-hover:scale-[1.01]">
                  
                  {}
                  <div className="flex items-center justify-center gap-1 mb-8">
                    {Array.from({
                  length: 5
                }).map((_, index) => <Star key={index} className={cn("size-6 md:size-7 transition-colors", index < rating ? "fill-primary text-primary drop-shadow-[0_0_8px_rgba(133,230,28,0.5)]" : "fill-muted text-muted")} />)}
                  </div>

                  {}
                  <blockquote className="mb-10 max-w-3xl mx-auto bg-card/40 backdrop-blur-md p-8 rounded-lg border border-white/10 shadow-2xl">
                    <p className="text-2xl md:text-[32px] lg:text-[32px] font-serif italic leading-tight text-foreground tracking-tight">
                      "{quote}"
                    </p>
                  </blockquote>

                  {}
                  <div className="flex flex-col items-center space-y-4">
                    <SafeHydrate><Avatar className="size-20 md:size-24 border-2 border-primary/30 shadow-xl grayscale hover:grayscale-0 transition-all duration-500">
                      {imageUrl ? <AvatarImage src={imageUrl} alt={authorName} className="object-cover" /> : <AvatarImage src={`/img/ceo_portrait_${i}.png`} alt={authorName} className="object-cover" />}
                      <AvatarFallback className="bg-muted text-primary text-xl font-default font-semibold">
                        {authorName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    
                    <div>
                      <h4 className="text-xl font-default font-bold text-foreground tracking-tight">
                        {authorName}
                      </h4>
                      {authorRole && <p className="text-sm md:text-base font-mono text-primary mt-1 uppercase tracking-widest">
                          {authorRole}
                        </p>}
                    </div>
                  </div>

                </div>
              </Link>
            </CarouselItem>;
      })}
      </CarouselContent>
      
      <CarouselPrevious className="hidden md:flex size-12 text-primary border-primary/40 bg-transparent hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 rounded-md -left-4 lg:-left-12" />
      <CarouselNext className="hidden md:flex size-12 text-primary border-primary/40 bg-transparent hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 rounded-md -right-4 lg:-right-12" />
    </Carousel>;
}
export default function ClientSuccessStories() {
  const wp_query_params = {
    post_type: "testimonial",
    per_page: 8,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="257148" id="client-success-stories" className="relative w-full py-20 md:py-32 overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute top-0 left-1/4 size-96 bg-secondary/10 blur-[120px] rounded-md pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 size-96 bg-primary/5 blur-[120px] rounded-md pointer-events-none" />
      
      <div className="page-container relative z-10">
        <div className="text-center mb-16">
          <span className="font-mono text-primary text-sm tracking-[0.3em] uppercase mb-4 block">Success Metrics</span>
          <h2 className="text-4xl md:text-5xl font-default font-bold tracking-tighter text-foreground">
            Trusted by Industry Leaders
          </h2>
        </div>
        
        <PostsProvider wp_query={{
        post_type: "testimonial",
        per_page: 8,
        orderby: "date",
        order: "desc"
      }}>
          <TestimonialConsumer />
        </PostsProvider>

        {}
        <div className="mt-20 flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-50 grayscale hover:grayscale-0 transition-all duration-700">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/client_logo_1.png" alt="Partner" className="h-8 w-auto object-contain" />
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/client_logo_2.png" alt="Partner" className="h-8 w-auto object-contain" />
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/client_logo_3.png" alt="Partner" className="h-8 w-auto object-contain" />
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/client_logo_4.png" alt="Partner" className="h-8 w-auto object-contain" />
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/client_logo_5.png" alt="Partner" className="h-8 w-auto object-contain" />
        </div>
      </div>
    </section>;
}